"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function SignatureGateContent() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const fallback = {
    name: "LUSH-SILK",
    series: "SEASONAL EDIT / THE SILK ARCHIVE / 2024",
    description: "Hand-loomed Italian silk, architectural drape, reinforced seams. 14 days from atelier to wardrobe. Limited release.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_silk_collection.webp",
    link: "/ShopPage"
  };
  const product = products && products.length > 0 ? products[0] : null;
  const displayName = product?.title?.toUpperCase() || fallback.name;
  const displayImage = product?.featured_image_url || fallback.image;
  const displayLink = product?.link || fallback.link;
  const nameChars = displayName.split("");
  const highlightIndex = Math.max(0, Math.floor(nameChars.length / 2));
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 relative">
      {}
      <div className="hidden lg:block absolute top-0 bottom-0 left-[58.333%] w-px" style={{
      backgroundColor: "var(--primary)",
      opacity: 0.4
    }} aria-hidden="true" />

      {}
      <div className="lg:col-span-7 lg:pr-0 relative">
        <div className="relative overflow-hidden">
          {loading ? <div className="w-full aspect-[3/4] bg-muted animate-pulse" /> : <img src={displayImage} data-wvc-srcset={product ? undefined : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_silk_collection.webp-wvc-srcset"} data-wvc-sizes={product ? undefined : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/lush_silk_collection.webp-wvc-sizes"} alt={`${displayName} - Editorial Fashion Piece`} className="w-full h-auto object-cover lg:h-[900px] shadow-lg" loading="lazy" decoding="async" />}
          {}
          <div className="hidden lg:block absolute top-0 right-0 translate-x-[8%] w-[15%] h-full bg-cover bg-center pointer-events-none" />
        </div>

        <p className="mt-6 text-[11px] tracking-[0.2em] uppercase font-mono" style={{
        color: "var(--muted-foreground)"
      }}>
          {fallback.series}
        </p>
      </div>

      {}
      <div className="lg:col-span-5 lg:pl-12 xl:pl-16 flex items-center relative z-10">
        <div className="w-full">
          {}
          <div className="flex items-center gap-3 mb-8">
            <span className="inline-block w-2 h-2" style={{
            backgroundColor: "var(--primary)"
          }} aria-hidden="true" />
            <span className="text-xs tracking-[0.25em] uppercase font-mono text-foreground/80">
              — Editorial Spotlight
            </span>
          </div>

          {}
          <p className="font-serif italic font-light text-2xl md:text-[28px] leading-snug mb-8 text-foreground/90">
            A study in silhouette and texture.
          </p>

          {}
          <h2 className="font-default font-black leading-[0.9] mb-10 tracking-tight" style={{
          fontSize: "clamp(3.5rem, 8vw, 7rem)",
          fontWeight: 900
        }}>
            {nameChars.map((char, i) => <span key={i} data-index={i} style={i === highlightIndex ? {
            color: "var(--primary)"
          } : undefined}>
                {char}
              </span>)}
          </h2>

          {}
          <p className="font-serif text-[17px] leading-[1.75] mb-12 max-w-[65ch]" style={{
          color: "var(--foreground)",
          opacity: 0.85
        }}>
            {fallback.description}
          </p>

          {}
          <div className="mb-12 border-t border-border">
            <div className="flex items-baseline justify-between py-4 border-b border-border">
              <span className="font-default text-xs tracking-[0.2em] uppercase text-muted-foreground">
                Composition
              </span>
              <span className="font-serif text-base text-foreground">
                100% Italian Silk
              </span>
            </div>
            <div className="flex items-baseline justify-between py-4 border-b border-border">
              <span className="font-default text-xs tracking-[0.2em] uppercase text-muted-foreground">
                Origin
              </span>
              <span className="font-serif text-base text-foreground">
                Milan, Italy
              </span>
            </div>
            <div className="flex items-baseline justify-between py-4 border-b border-border">
              <span className="font-default text-xs tracking-[0.2em] uppercase text-muted-foreground">
                Availability
              </span>
              <span className="font-serif text-base text-foreground">
                Limited Edition
              </span>
            </div>
          </div>

          {}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {product ? <AddToCartButton productId={product.id} className="w-full sm:w-auto" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-sm font-default font-semibold tracking-[0.15em] uppercase shadow-lg" text="Add to Wardrobe" showQuantity={false} /> : <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 h-auto text-sm font-default font-semibold tracking-[0.15em] uppercase shadow-lg">
                <Link to="/ShopPage">Add to Wardrobe</Link>
              </Button>}

            <Link to={displayLink} className="group inline-flex items-center gap-2 text-sm font-default tracking-[0.15em] uppercase text-foreground hover:text-primary transition-colors">
              View Collection
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>;
}
export default function Collection() {
  const wp_query_params = {
    per_page: 1,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2949" id="collection-spotlight" className="bg-background text-foreground py-24 md:py-32 lg:py-[200px] px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        <EcommerceProductsProvider wp_query={{
        per_page: 1,
        orderby: "date",
        order: "desc"
      }}>
          <SignatureGateContent />
        </EcommerceProductsProvider>
      </div>
    </section>;
}